/**
 * useDbUser — DB-backed admin flag.
 *
 * Distinct from `useIsAdmin` (client-side email-allowlist gate for
 * unpolished-UX surfaces). This hook reads the authoritative `is_admin`
 * column on the `users` table, via the backend `UserResponse` shape, for
 * gating actually-sensitive admin features (e.g. org impersonation).
 *
 * Backend contract (verified — see .superpowers/sdd/task-6-brief.md):
 *   - `POST /auth/sync` is POST-only. It creates/updates the DB user row
 *     and two-way-syncs `is_admin`, and MUST be called first (with a
 *     NON-EMPTY body — the BFF drops empty `{}` bodies) on the first
 *     Firebase-authenticated load.
 *   - `GET /auth/me` 404s ("Please call /auth/sync first") for a user
 *     that hasn't synced yet, so it is unsafe to call before `/auth/sync`
 *     has succeeded at least once.
 *   - There is no `GET /auth/sync` — that route is POST-only. Never call it.
 *
 * This hook keeps to the minimal `POST /auth/sync` path on every mount
 * (idempotent server-side); a `GET /auth/me` refresh path can be layered
 * on later by callers that need it without changing this contract.
 *
 * ## Request dedupe
 *
 * 12 components mount this hook per pageload (7 via `useIsAdmin`, 2 directly,
 * 3 via `useHasLabelNegotiations`). Each used to POST `/auth/sync` on its own,
 * which was not merely wasteful — the results could DISAGREE. If a child's
 * sync resolved while the parent's failed, the child would enable a control
 * the parent's submit-time coercion then refused, and the search silently ran
 * without it.
 *
 * The in-flight/resolved promise is therefore shared at module scope, keyed by
 * `uid | impersonated-org`. Both halves of that key are load-bearing:
 *
 *   - `uid` — `AuthContext.signOut` clears React state WITHOUT reloading, so a
 *     different user in the same tab must not inherit the previous one's
 *     `is_admin`.
 *   - impersonated org — `ImpersonationContext.start()` also does NOT reload
 *     (only `exit()` does, via `window.location.reload()`), so module state
 *     survives it. The `X-Impersonate-Org` header is read per-request from
 *     sessionStorage and so never reaches this hook's dep array. Without the
 *     org in the key, a component mounting AFTER impersonation began — e.g.
 *     `ResearchPanel` when the user switches to Investigator — would be served
 *     the ADMIN'S OWN org answer instead of the impersonated tenant's. That is
 *     the same wrong-tenant hazard the backend guards by scoping on the
 *     effective org rather than `users.organization_id`.
 *
 * Failures are NEVER cached: one transient blip would otherwise grey the
 * Label Negotiations controls for every consumer for the rest of the session.
 *
 * KNOWN GAP (pre-existing, unchanged here): components ALREADY MOUNTED when
 * impersonation starts do not re-sync, because the effect's deps are
 * `[user, authLoading]` and neither changes. They keep the admin's own flags
 * until they remount or the page reloads. Fixing that means putting the
 * impersonation org in the dep array, which changes behaviour for all 12
 * consumers — deliberately out of scope for the dedupe.
 */
import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authApiClient } from '../services/authApi';
import { getImpersonationHeaders } from '../services/impersonationHeaders';

interface DbUserState {
  isAdmin: boolean;
  /** Whether this user's ORG owns at least one draft label. Drives the greyout
   *  of the three Label Negotiations controls (Cogni's ``draft_review`` pill,
   *  Investigator's ``label_negotiations`` pill, and Cogni's "Include draft
   *  label negotiations" checkbox) — all three are dead UI without drafts.
   *  Backed by an org-scoped EXISTS on ``label_versions``; see
   *  ``_org_has_label_negotiations`` in backend/app/api/auth_routes.py. */
  hasLabelNegotiations: boolean;
  loading: boolean;
}

interface AuthSyncResponse {
  is_admin: boolean;
  has_label_negotiations?: boolean;
}

/** Shared in-flight/resolved sync, keyed by `uid | impersonated-org`. */
let syncCache: { key: string; promise: Promise<AuthSyncResponse> } | null = null;

/** Identity the sync result belongs to. A change on EITHER axis must re-sync
 *  — see the module docstring for why neither reaches the effect's deps. */
function syncKey(uid: string): string {
  const org = getImpersonationHeaders()['X-Impersonate-Org'] ?? '';
  return `${uid}|${org}`;
}

function syncOnce(uid: string, displayName: string | null, photoUrl: string | null) {
  const key = syncKey(uid);
  if (syncCache && syncCache.key === key) return syncCache.promise;

  const promise = authApiClient
    // Send a NON-EMPTY body. The BFF proxy only forwards a request body when
    // Object.keys(body).length > 0 (frontend/functions/src/index.ts), so an
    // empty `{}` is dropped and the backend's required `user_data:
    // UserSyncRequest` param 422s — which silently left isAdmin=false and hid
    // the Admin menu for every user. display_name/photo_url are the exact
    // (nullable) UserSyncRequest fields, so this both fixes the drop AND
    // refreshes those fields from Firebase.
    .post<AuthSyncResponse>('/auth/sync', { display_name: displayName, photo_url: photoUrl })
    .then((response) => response.data)
    .catch((err) => {
      // Evict so the next mount retries rather than inheriting one blip.
      if (syncCache?.key === key) syncCache = null;
      throw err;
    });

  syncCache = { key, promise };
  return promise;
}

/** Drop the shared sync. Called on sign-out, and by tests — module state
 *  outlives a test, so without a reset every test after the first would see
 *  zero requests and quietly stop asserting anything. */
export function __resetDbUserCacheForTests(): void {
  syncCache = null;
}

export function useDbUser(): DbUserState {
  const { user, loading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [hasLabelNegotiations, setHasLabelNegotiations] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Firebase auth state hasn't resolved yet — wait rather than firing a
    // sync with no user.
    if (authLoading) {
      return;
    }

    if (!user) {
      // Signed out. Drop the shared result: signOut does not reload, so the
      // same uid signing back in would otherwise inherit a stale is_admin.
      syncCache = null;
      setIsAdmin(false);
      setHasLabelNegotiations(false);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    syncOnce(user.uid, user.displayName, user.photoURL)
      .then((data) => {
        if (!cancelled) {
          setIsAdmin(Boolean(data.is_admin));
          setHasLabelNegotiations(Boolean(data.has_label_negotiations));
        }
      })
      .catch(() => {
        // Sync failure degrades to non-admin (safer default) rather than
        // throwing into the render tree. Same for the drafts flag: greying a
        // control we cannot vouch for beats offering a search that 500s.
        if (!cancelled) {
          setIsAdmin(false);
          setHasLabelNegotiations(false);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [user, authLoading]);

  return { isAdmin, hasLabelNegotiations, loading };
}
